import { useEffect, useRef, useState } from 'react'

import { ThemeMode, useTheme } from '@/design-system/ThemeProvider'
import { FOCUS } from '@/design-system/ui'

/// Header theme control. A round icon button that mirrors the active mode (monitor / sun / moon) and
/// opens a small three-row menu — Automatic, Light, Dark — with a check on the active row. Closes on
/// outside-click / Esc and is fully keyboard accessible.

function MonitorIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="16"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
      viewBox="0 0 24 24"
      width="16"
      {...props}
    >
      <rect height="12.5" rx="2" width="19" x="2.5" y="4" />
      <path d="M8 20.5h8M12 16.5v4" />
    </svg>
  )
}

function SunIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="16"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
      viewBox="0 0 24 24"
      width="16"
      {...props}
    >
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2.5v2.4M12 19.1v2.4M4.9 4.9l1.7 1.7M17.4 17.4l1.7 1.7M2.5 12h2.4M19.1 12h2.4M4.9 19.1l1.7-1.7M17.4 6.6l1.7-1.7" />
    </svg>
  )
}

function MoonIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="16"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
      viewBox="0 0 24 24"
      width="16"
      {...props}
    >
      <path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z" />
    </svg>
  )
}

function CheckIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="15"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.9"
      viewBox="0 0 24 24"
      width="15"
      {...props}
    >
      <path d="M5 12.5 10 17.5 19 7" />
    </svg>
  )
}

const OPTIONS: {
  mode: ThemeMode
  label: string
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
}[] = [
  { mode: 'auto', label: 'Automatic', Icon: MonitorIcon },
  { mode: 'light', label: 'Light', Icon: SunIcon },
  { mode: 'dark', label: 'Dark', Icon: MoonIcon },
]

export function ThemeToggle() {
  const { mode, resolved, setMode } = useTheme()
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    function onPointer(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Fixed-position the menu from the button's rect so it escapes the header pill's overflow clip.
  useEffect(() => {
    if (!open) return
    function place() {
      const r = btnRef.current?.getBoundingClientRect()
      if (r) setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open])

  const TriggerIcon = resolved === 'dark' ? MoonIcon : SunIcon

  return (
    <div ref={wrapRef} style={{ position: 'relative', flex: 'none' }}>
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Theme"
        className={FOCUS}
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={(e) =>
          (e.currentTarget.style.background =
            'color-mix(in srgb, var(--surface-quiet) 90%, var(--ink))')
        }
        onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--surface-quiet)')}
        ref={btnRef}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: 999,
          background: 'var(--surface-quiet)',
          border: 'none',
          color: 'var(--ink)',
          cursor: 'pointer',
          transition: 'background .2s ease',
        }}
        title="Theme"
        type="button"
      >
        <TriggerIcon />
      </button>

      {open && pos && (
        <div
          aria-label="Theme"
          role="menu"
          style={{
            position: 'fixed',
            top: pos.top,
            right: pos.right,
            zIndex: 1000,
            minWidth: 176,
            background: 'var(--surface)',
            border: '1px solid var(--rule-light)',
            borderRadius: 16,
            padding: 4,
            boxShadow: '0 8px 28px color-mix(in srgb, var(--ink) 14%, transparent)',
          }}
        >
          {OPTIONS.map((opt, i) => {
            const active = opt.mode === mode
            const { Icon } = opt
            return (
              <button
                aria-checked={active}
                className={FOCUS}
                key={opt.mode}
                onClick={() => {
                  setMode(opt.mode)
                  setOpen(false)
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-quiet)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                role="menuitemradio"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: 12,
                  background: 'transparent',
                  border: 'none',
                  borderTop: i === 0 ? 'none' : '1px solid var(--rule-light)',
                  color: active ? 'var(--ink)' : 'var(--secondary)',
                  fontFamily: 'var(--font-nav)',
                  fontSize: 13,
                  fontWeight: 400,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
                type="button"
              >
                <Icon style={{ flex: 'none', color: active ? 'var(--ink)' : 'var(--muted)' }} />
                <span style={{ flex: 1 }}>{opt.label}</span>
                <span style={{ width: 15, height: 15, flex: 'none', color: 'var(--ink)' }}>
                  {active && <CheckIcon />}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
